'use client';

import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Subscription {
  id: string;
  userId: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  status: 'active' | 'trialing' | 'canceled' | 'past_due' | 'inactive';
  currentPeriodEnd: string | null;
  /** Access granted by decision rather than bought. Outranks status. */
  comped: boolean;
}

interface SubscriptionContextValue {
  subscription: Subscription | null;
  loading: boolean;
  isSubscribed: boolean;
  statusLabel: string;
  startCheckout: (affiliateCode?: string) => Promise<void>;
  checkoutLoading: boolean;
  checkoutError: string | null;
  checkoutOpenedInTab: boolean;
  refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextValue>({
  subscription: null,
  loading: true,
  isSubscribed: false,
  statusLabel: '',
  startCheckout: async () => {},
  checkoutLoading: false,
  checkoutError: null,
  checkoutOpenedInTab: false,
  refreshSubscription: async () => {},
});

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
};

function rowToSubscription(row: Record<string, unknown>): Subscription {
  return {
    id: row.id as string,
    userId: row.user_id as string,
    stripeCustomerId: (row.stripe_customer_id as string) || null,
    stripeSubscriptionId: (row.stripe_subscription_id as string) || null,
    status: (row.status as Subscription['status']) || 'inactive',
    currentPeriodEnd: (row.current_period_end as string) || null,
    comped: row.comped === true,
  };
}

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutOpenedInTab, setCheckoutOpenedInTab] = useState(false);
  // Track the last user id we fetched for, so we re-fetch when user changes
  const lastFetchedUserId = useRef<string | null>(null);

  /**
   * @param silent Refresh the data without announcing a load.
   *
   * A background re-check must not raise `loading`. Pages gate their whole
   * tree on it, so flipping it swaps a live page — form contents and all —
   * for a spinner, and the user gets a blank form back a moment later. The
   * flag exists to say "there is nothing to show yet", which stops being true
   * after the first successful fetch.
   */
  const fetchSubscription = useCallback(
    async (silent = false) => {
      if (!user) {
        setSubscription(null);
        setLoading(false);
        lastFetchedUserId.current = null;
        return;
      }
      if (!silent) setLoading(true);
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('[Subscription] Failed to fetch:', error.message);
          // A failed background check leaves the last known answer alone.
          // Blanking it would revoke a paying customer's access because one
          // request lost the network for a second.
          if (!silent) setSubscription(null);
        } else {
          setSubscription(data ? rowToSubscription(data) : null);
          lastFetchedUserId.current = user.id;
        }
      } catch (err) {
        console.error('[Subscription] Fetch error:', err);
        if (!silent) setSubscription(null);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [user]
  );

  // Fetch on mount and whenever the signed-in identity changes. Keyed on the
  // id, not the user object: Supabase hands over a fresh object on every token
  // refresh, and refetching on those is what made the app churn.
  const userId = user?.id ?? null;
  useEffect(() => {
    void fetchSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Re-check when the tab comes back, because the answer may have changed in
  // a Stripe tab while this one was hidden.
  //
  // Only `visibilitychange`. It already covers tab switching, and `focus`
  // fires for window focus too — clicking back into the browser from another
  // app ran the whole thing a second time for no new information.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible' && userId) {
        void fetchSubscription(true);
      }
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // `comped` is access granted by decision rather than bought, and it outranks
  // status: Stripe knows nothing about the decision, so leaving it to status
  // would revoke it the next time that customer's subscription changed.
  const isSubscribed =
    subscription?.comped === true ||
    subscription?.status === 'active' ||
    subscription?.status === 'trialing';

  const statusLabel = (() => {
    if (!subscription) return '';
    if (subscription.status === 'trialing') return 'Trial active';
    if (subscription.status === 'active') return 'Pro active';
    if (subscription.status === 'past_due') return 'Payment past due';
    if (subscription.status === 'canceled') return 'Canceled';
    return '';
  })();

  const startCheckout = useCallback(async (affiliateCode?: string) => {
    setCheckoutLoading(true);
    setCheckoutError(null);
    setCheckoutOpenedInTab(false);
    try {
      // The code is a hint, not a requirement. The server decides whether it
      // resolves to anything, and an unusable one never blocks the purchase.
      const res = await fetch('/api/stripe/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(affiliateCode ? { affiliateCode } : {}),
      });
      const data = await res.json();

      console.log('[Checkout] Response status:', res.status, '| Data:', data);

      if (data.url) {
        // Detect if running inside an iframe (Rocket preview / embedded context)
        const isEmbedded = typeof window !== 'undefined' && window.self !== window.top;

        if (isEmbedded) {
          // Open Stripe in a new top-level tab so it doesn't render blank inside the iframe
          window.open(data.url, '_blank', 'noopener,noreferrer');
          setCheckoutOpenedInTab(true);
          setCheckoutLoading(false);
        } else {
          window.location.href = data.url;
        }
      } else {
        const errMsg = data.error || 'Failed to start checkout. Please try again.';
        console.error('[Checkout] No URL returned:', errMsg);
        setCheckoutError(errMsg);
        setCheckoutLoading(false);
      }
    } catch (err) {
      console.error('[Checkout] Network error:', err);
      setCheckoutError('Network error. Please check your connection and try again.');
      setCheckoutLoading(false);
    }
  }, []);

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        loading,
        isSubscribed,
        statusLabel,
        startCheckout,
        checkoutLoading,
        checkoutError,
        checkoutOpenedInTab,
        refreshSubscription: () => fetchSubscription(),
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}
